// ── usePersonsAndWorkflow — 최근계약현황 상태 & API ──────────────────
// (미팅인계 담당자 상태·API 는 미팅지도 관리 useMeetingMapSettings 로 이관 — 2026-09-01)
import { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';

export function usePersonsAndWorkflow() {
  const { showToast } = useToast();
  // 지역 색상 기준이 방문빈도 관리로 옮겨가면서(2026-09-17) 이 훅은 최근계약현황만 다룬다.
  //   저장 상태는 직원별로 rcSavingUserId 가 따로 가진다.
  const saving = false;

  // ── 최근계약현황 직원별 설정 ──────────────────────────────────────────────────
  const [rcConfig,        setRcConfig]        = useState(null);  // { ticket_types, assigned_users }
  const [rcConfigLoading, setRcConfigLoading] = useState(false);
  // rcUserSettings: { [userId]: { months: number, ticketTypeIds: Set<number> } }
  const [rcUserSettings,  setRcUserSettings]  = useState({});
  const [rcSavingUserId,  setRcSavingUserId]  = useState(null); // 저장 중인 직원 ID

  // ── 최근계약현황 직원별 설정 API ─────────────────────────────────────────────
  async function fetchRcConfig() {
    setRcConfigLoading(true);
    const r = await apiClient.get('/api/admin/settings/recent-contracts-config');
    setRcConfigLoading(false);
    if (!r.ok) { showToast('최근계약현황 설정을 불러오지 못했습니다.', 'error'); return; }
    const data = r.data?.data;
    if (!data) return;
    setRcConfig(data);
    // 직원별 설정 초기화: assigned_users 배열에서 months + ticket_type_ids 추출
    const initSettings = {};
    (data.assigned_users || []).forEach((u) => {
      initSettings[u.id] = {
        months:        u.months ?? 3,
        ticketTypeIds: new Set((u.ticket_type_ids || []).map(Number)),
      };
    });
    setRcUserSettings(initSettings);
  }

  // 직원별 개월수 변경 (로컬 상태)
  function setRcUserMonths(userId, months) {
    const num = parseInt(months, 10);
    setRcUserSettings((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], months: isNaN(num) ? '' : num },
    }));
  }

  // 직원별 상품 토글 (로컬 상태)
  function toggleRcUserTicketType(userId, ttId) {
    setRcUserSettings((prev) => {
      const cur = prev[userId] || { months: 3, ticketTypeIds: new Set() };
      const next = new Set(cur.ticketTypeIds);
      if (next.has(ttId)) next.delete(ttId); else next.add(ttId);
      return { ...prev, [userId]: { ...cur, ticketTypeIds: next } };
    });
  }

  // 직원별 설정 저장
  async function saveRcUserSettings(userId) {
    const settings = rcUserSettings[userId];
    if (!settings) return;
    setRcSavingUserId(userId);
    const r = await apiClient.put(`/api/admin/settings/recent-contracts-user/${userId}`, {
      months:          settings.months,
      ticket_type_ids: [...(settings.ticketTypeIds || [])],
    });
    setRcSavingUserId(null);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    showToast('설정이 저장되었습니다.', 'success');
  }

  return {
    saving,
    // 지역 색상 기준
    // 최근계약현황 직원별 설정
    rcConfig, rcConfigLoading,
    rcUserSettings, rcSavingUserId,
    fetchRcConfig, setRcUserMonths, toggleRcUserTicketType, saveRcUserSettings,
  };
}
